import { useRef } from "react";

/**
 * Wraps real, embedded ConfirmPH page components so the walkthrough can stay
 * fully self-contained: every link click and form submit inside is
 * intercepted (capture phase, before React Router's own handlers run) and
 * reported to the walkthrough's local state instead of causing a real
 * navigation or a real network write. This is why the real presentational
 * components (real <Link>s, real forms) can be reused as-is here with zero
 * risk of the browser leaving the homepage.
 *
 * Does NOT intercept plain `window.open`/`target="_blank"` external links
 * (e.g. Facebook/Messenger contact links): those open a new tab rather
 * than navigating the walkthrough away, so they're left alone.
 */
export default function DemoSandbox({ children, onNavigate, onSubmit, testId, passthroughSubmitTestIds = [] }) {
  const ref = useRef(null);

  function handleClick(e) {
    const a = e.target.closest && e.target.closest("a[href]");
    if (!a || !ref.current || !ref.current.contains(a)) return;
    if (a.target === "_blank" || a.getAttribute("href")?.startsWith("http")) return; // real external links stay real
    e.preventDefault();
    e.stopPropagation();
    onNavigate?.(a.getAttribute("href"), a);
  }

  function handleSubmit(e) {
    if (!ref.current || !ref.current.contains(e.target)) return;
    // Some forms (e.g. ReviewInvite's) are already safe: forked to accept
    // an onSubmit prop that never hits the real network, so their own
    // real handler should run untouched rather than being swallowed here.
    const testIdOfForm = e.target.getAttribute?.("data-testid");
    if (passthroughSubmitTestIds.includes(testIdOfForm)) return;
    e.preventDefault();
    e.stopPropagation();
    onSubmit?.(e.target);
  }

  return (
    <div ref={ref} onClickCapture={handleClick} onSubmitCapture={handleSubmit} data-testid={testId}>
      {children}
    </div>
  );
}
